// ScenarioEdit.tsx - Edit scenario component with validation

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Scenario, ScenarioFormData } from '../../types/ScenarioManagement';
import SimpleScenarioForm from './SimpleScenarioForm';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';

interface ScenarioEditProps {
  scenarioId: string;
  onSave: (data: ScenarioFormData) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => void;
}

const ScenarioEdit: React.FC<ScenarioEditProps> = ({
  scenarioId,
  onSave,
  onCancel,
  onDelete
}) => {
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadScenario();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  const loadScenario = async () => {
    if (!scenarioId) {
      setError('No scenario ID provided');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const persistenceService = JSONPersistenceService.getInstance();
      const data = await persistenceService.loadData();
      
      if (!data) {
        throw new Error('No scenario data found');
      }
      
      // Find the scenario by ID
      const scenarioEntry = data.scenarios.find(([id]) => id === scenarioId);
      if (!scenarioEntry) {
        throw new Error('Scenario not found');
      }
      
      const loadedScenario = scenarioEntry[1] as Scenario;
      
      // Ensure the scenario has proper default values for content
      if (!loadedScenario.content) {
        loadedScenario.content = {
          objectives: [],
          successCriteria: [],
          availableResources: [],
          constraints: [],
          executiveSummary: ''
        };
      } else {
        // Ensure all required fields exist in content
        const defaultContent = {
          objectives: [],
          successCriteria: [],
          availableResources: [],
          constraints: [],
          executiveSummary: ''
        };
        loadedScenario.content = {
          ...defaultContent,
          ...loadedScenario.content
        };
      }
      
      setScenario(loadedScenario);
    } catch (err) {
      console.error('Error loading scenario:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scenario. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (data: ScenarioFormData) => {
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving scenario:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-950 to-navy-900 py-8 px-4">
        <div className="w-full">
          <div className="text-center py-16">
            <motion.div
              className="w-12 h-12 mb-4 mx-auto"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <svg 
                className="w-full h-full text-professional-blue-500" 
                viewBox="0 0 24 24" 
                fill="none"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </motion.div>
            <p className="text-slate-300">Loading scenario...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-950 to-navy-900 py-8 px-4">
        <div className="w-full">
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" className="text-red-400">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <h3 className="text-xl font-medium text-white mb-2">Error Loading Scenario</h3>
            <p className="text-slate-300 mb-6">{error}</p>
            <button
              onClick={loadScenario}
              className="bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!scenario) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 to-navy-900 py-8 px-4 lg:px-8">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-white mb-2">Edit Scenario</h1>
            <p className="text-slate-300">Update scenario details and content</p>
          </div>
          
          {onDelete && (
            <button
              onClick={onDelete}
              className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
              Delete Scenario
            </button>
          )}
        </div>

        {/* Scenario Info */}
        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4 mb-6 flex items-center gap-4">
          <span className="text-3xl">{scenario.icon || '🎯'}</span>
          <div>
            <h2 className="text-lg font-semibold text-white">{scenario.title || 'Untitled Scenario'}</h2>
            <p className="text-sm text-slate-400">
              {scenario.createdAt && `Created ${new Date(scenario.createdAt).toLocaleDateString()}`}
              {scenario.createdAt && scenario.updatedAt && ' • '}
              {scenario.updatedAt && `Last updated ${new Date(scenario.updatedAt).toLocaleDateString()}`}
              {scenario.version && ` • Version ${scenario.version}`}
            </p>
          </div>
        </div>

        {/* Form */}
        <SimpleScenarioForm
          initialData={scenario}
          onSubmit={handleSave}
          onCancel={onCancel}
          mode="edit"
        />
      </div>
    </div>
  );
};

export default ScenarioEdit;